import Editor from "~/js/editor";
import { $div } from "~/js/utilities/TurboDom";

export interface IElementResizer {
	y: number;
	x: number;

	width: number;
	height: number;

	domElement(): HTMLElement;
}

export abstract class ElementResizer implements IElementResizer {
	y: number = 0;
	x: number = 0;

	width: number = 0;
	height: number = 0;
	get boundary() {
		return {
			left: this.x,
			right: this.x + this.width,
			top: this.y,
			bottom: this.y + this.height
		};
	}

	getSize() {
		return {
			width: this.width,
			height: this.height
		};
	}

	getPosition() {
		return {
			x: this.x,
			y: this.y
		};
	}

	setPosition(pos: { x: number; y: number }) {
		const { x, y } = pos;

		this.setXPosition(x);
		this.setYPosition(y);
	}

	setYPosition(y: number) {
		this.y = y;
		this.domElement().style.top = `${y}px`;
	}

	setXPosition(x: number) {
		this.x = x;
		this.domElement().style.left = `${x}px`;
	}

	setWidth(px: number) {
		this.width = px;
		this.domElement().style.width = `${px}px`;
	}

	setHeight(px: number) {
		this.height = px;
		this.domElement().style.height = `${px}px`;
	}
	abstract domElement(): HTMLElement;
}

export interface IBorderResizer {
	MIN_HEIGHT: number;
	MIN_WIDTH: number;

	startMouseX: number;
	startMouseY: number;

	startHeight: number;
	startWidth: number;

	startElY: number;
	startElX: number;

	zoomScale: number;
}

export abstract class BorderResizer extends ElementResizer implements IBorderResizer {
	MIN_HEIGHT: number = 0;
	MIN_WIDTH: number = 0;
	startMouseX: number = 0;
	startMouseY: number = 0;
	startHeight: number = 0;
	startWidth: number = 0;
	startElY: number = 0;
	startElX: number = 0;
	zoomScale: number = 1;
	calculateDx(x: number) {
		return (x - this.startMouseX) / this.zoomScale;
	}

	calculateDy(y: number) {
		return (y - this.startMouseY) / this.zoomScale;
	}

	resizeBottomHeight(e: MouseEvent) {
		const dy = this.calculateDy(e.clientY);

		this.setHeight(Math.max(this.startHeight + dy, this.MIN_HEIGHT));
	}

	resizeTopHeight(e: MouseEvent) {
		const dy = this.calculateDy(e.clientY);

		this.setHeight(Math.max(this.startHeight - dy, this.MIN_HEIGHT));
		this.setYPosition(this.startElY + dy);
	}

	resizeLeftWidth(e: MouseEvent) {
		const dx = this.calculateDx(e.clientX);
		this.setWidth(Math.max(this.startWidth - dx, this.MIN_WIDTH));
		this.setXPosition(this.startElX + dx);
	}

	resizeRightWidth(e: MouseEvent) {
		const dx = this.calculateDx(e.clientX);
		this.setWidth(Math.max(this.startWidth + dx, this.MIN_WIDTH));
	}

	resizeTopRightCorner(e: MouseEvent) {
		this.resizeRightWidth(e);
		this.resizeTopHeight(e);
	}

	resizeBottomRightCorner(e: MouseEvent) {
		this.resizeRightWidth(e);
		this.resizeBottomHeight(e);
	}

	resizeBottomLeftCorner(e: MouseEvent) {
		this.resizeLeftWidth(e);
		this.resizeBottomHeight(e);
	}

	resizeTopLeftCorner(e: MouseEvent) {
		this.resizeLeftWidth(e);
		this.resizeTopHeight(e);
	}
}

export interface INodeSelector {
	x: number;
	y: number;

	height: number;
	width: number;

	selectorEl: HTMLDivElement;
}

export class NodeSelector extends BorderResizer implements INodeSelector {
	selectorEl = $div("nodeSelector hiddenNodeSelector");
	x = 0;
	y = 0;
	width = 0;
	height = 0;
	MIN_HEIGHT = 0;
	MIN_WIDTH = 0;

	mouseDownHandler(e: MouseEvent) {
		this.startMouseX = e.clientX;
		this.startMouseY = e.clientY;

		this.zoomScale = Editor.context.graph.navigation.getZoomScale();

		const { x, y } = Editor.context.graph.navigation.clientToGraphSpace(e.clientX, e.clientY);
		this.startElX = x;
		this.startElY = y;
		this.setPosition({
			x,
			y
		});
		this.setHeight(0);
		this.setWidth(0);
		this.startWidth = 0;
		this.startHeight = 0;

		const mouseMoveHandler = (e: MouseEvent) => {
			this.mouseMoveHandler(e);
		};
		const mouseUpHandler = (_e: MouseEvent) => {
			this.mouseUpHandler(mouseMoveHandler, mouseUpHandler);
		};
		document.addEventListener("mousemove", mouseMoveHandler);
		document.addEventListener("mouseup", mouseUpHandler);
		this.domElement().classList.remove("hiddenNodeSelector");
	}

	mouseMoveHandler(e: MouseEvent) {
		const eventXPos = e.clientX;
		const eventYPos = e.clientY;

		if (eventXPos > this.startMouseX) {
			if (eventYPos > this.startMouseY) {
				this.resizeBottomRightCorner(e);
				return;
			}
			this.resizeTopRightCorner(e);
			return;
		}
		if (eventYPos > this.startMouseY) {
			this.resizeBottomLeftCorner(e);
			return;
		}
		this.resizeTopLeftCorner(e);
	}

	domElement() {
		return this.selectorEl;
	}

	calculateBound() {
		const { left, right, top, bottom } = this.domElement().getBoundingClientRect();

		return {
			top,
			bottom,
			left,
			right
		};
	}

	mouseUpHandler(moveHandler: (e: MouseEvent) => void, upHandler: (e: MouseEvent) => void) {
		document.removeEventListener("mousemove", moveHandler);
		document.removeEventListener("mouseup", upHandler);
		this.domElement().classList.add("hiddenNodeSelector");
		const bound = this.calculateBound();
		Editor.context.graph.selectNodesInBound(bound);
		Editor.context.graph.selectCommentFrameInBound(bound);
	}
}
